import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { tileButton } from '@/components/Board/commonToolbarStyles';
export const NumberInput = ({ value, min, max, step = 1, disabled = false, width = 60, onChange, }) => {
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const lastUserValueRef = useRef(null);
    // Синхронизируем локальное состояние с внешним значением
    useEffect(() => {
        const valueStr = Number.isFinite(value) ? String(value) : '';
        // Если инпут не в фокусе, всегда синхронизируем
        if (!focused) {
            setInputValue(valueStr);
            lastUserValueRef.current = null;
            return;
        }
        // Если инпут в фокусе, синхронизируем только если значение изменилось извне
        // (т.е. оно не соответствует последнему значению, установленному пользователем)
        if (lastUserValueRef.current === null || lastUserValueRef.current !== value) {
            setInputValue(valueStr);
            lastUserValueRef.current = null;
        }
    }, [value, focused]);
    const clamp = useCallback((v) => {
        let nv = v;
        if (typeof min === 'number')
            nv = Math.max(min, nv);
        if (typeof max === 'number')
            nv = Math.min(max, nv);
        return nv;
    }, [min, max]);
    const handleMinus = () => {
        if (disabled)
            return;
        const next = clamp((Number.isFinite(value) ? value : 0) - step);
        // Ограничиваем до 2 знаков после запятой
        const rounded = Math.round(next * 100) / 100;
        onChange(rounded);
    };
    const handlePlus = () => {
        if (disabled)
            return;
        const next = clamp((Number.isFinite(value) ? value : 0) + step);
        // Ограничиваем до 2 знаков после запятой
        const rounded = Math.round(next * 100) / 100;
        onChange(rounded);
    };
    const smallBtn = useMemo(() => (Object.assign(Object.assign({}, tileButton(false)), { minWidth: 0, width: 10, height: 14, borderRadius: 0, lineHeight: 1, padding: '2px 8px', margin: 0, fontSize: 10, fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: 'none' })), []);
    const handleWheel = (e) => {
        if (disabled)
            return;
        // prevent page scroll when adjusting number via wheel
        e.preventDefault();
        const direction = e.deltaY;
        if (direction < 0) {
            const next = clamp((Number.isFinite(value) ? value : 0) + step);
            // Ограничиваем до 2 знаков после запятой
            const rounded = Math.round(next * 100) / 100;
            onChange(rounded);
            return;
        }
        if (direction > 0) {
            const next = clamp((Number.isFinite(value) ? value : 0) - step);
            // Ограничиваем до 2 знаков после запятой
            const rounded = Math.round(next * 100) / 100;
            onChange(rounded);
        }
    };
    return (_jsxs("div", { style: {
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'stretch',
            width,
            height: 32,
            borderRadius: 8,
            background: '#1B1E1F99',
            border: '1px solid rgba(255,255,255,0.08)',
            boxSizing: 'border-box',
            overflow: 'hidden',
            opacity: disabled ? 0.6 : 1,
        }, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), onWheel: handleWheel, children: [_jsx("input", { type: "text", inputMode: "decimal", value: focused ? inputValue : Number.isFinite(value) ? String(value) : '', onChange: (e) => {
                    const raw = e.target.value.replace(',', '.');
                    // Разрешаем ввод пустой строки и промежуточных значений (например, "2.", "-", ".")
                    setInputValue(raw);
                    // Проверяем, является ли значение валидным числом
                    if (raw === '' || raw === '-' || raw === '.' || raw === '-.') {
                        return; // Разрешаем промежуточные состояния во время ввода
                    }
                    const parsed = Number(raw);
                    if (!isNaN(parsed) && raw.trim() !== '') {
                        // Ограничиваем до 2 знаков после запятой
                        const rounded = Math.round(parsed * 100) / 100;
                        const clampedValue = clamp(rounded);
                        // Сохраняем значение, которое мы устанавливаем, чтобы не перезаписывать его в useEffect
                        lastUserValueRef.current = clampedValue;
                        onChange(clampedValue);
                    }
                }, disabled: disabled, onFocus: (e) => {
                    setFocused(true);
                    lastUserValueRef.current = null;
                    setInputValue(Number.isFinite(value) ? String(value) : '');
                    e.target.select();
                }, onBlur: () => {
                    setFocused(false);
                    lastUserValueRef.current = null;
                    // При потере фокуса применяем значение или возвращаем текущее
                    const raw = inputValue.replace(',', '.');
                    const parsed = Number(raw);
                    if (!isNaN(parsed) && raw.trim() !== '') {
                        const rounded = Math.round(parsed * 100) / 100;
                        const clampedValue = clamp(rounded);
                        lastUserValueRef.current = clampedValue;
                        onChange(clampedValue);
                    }
                    setInputValue('');
                }, style: {
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    background: 'transparent',
                    color: '#B6BABD',
                    border: 'none',
                    outline: 'none',
                    padding: '2px 4px',
                    textAlign: 'center',
                    fontSize: '0.8rem',
                }, "aria-label": "\u0427\u0438\u0441\u043B\u043E\u0432\u043E\u0435 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0435", onWheel: handleWheel }), _jsxs("div", { style: {
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 0,
                    flexShrink: 0,
                    width: 20,
                    height: '100%',
                    opacity: (hovered || focused) && !disabled ? 1 : 0,
                    transition: 'opacity 120ms ease',
                    pointerEvents: (hovered || focused) && !disabled ? 'auto' : 'none',
                }, onWheel: handleWheel, children: [_jsx("button", { type: "button", onClick: handlePlus, style: smallBtn, "aria-label": "\u0423\u0432\u0435\u043B\u0438\u0447\u0438\u0442\u044C", children: "\uFF0B" }), _jsx("button", { type: "button", onClick: handleMinus, style: smallBtn, "aria-label": "\u0423\u043C\u0435\u043D\u044C\u0448\u0438\u0442\u044C", children: "\u2212" })] })] }));
};
export default NumberInput;
